import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPinIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Modal, Spinner, Badge } from '../ui';
import { staffNetworkApi } from '../../api/staffNetwork';
import type { StaffEngagements, StaffDayPlanEntry } from '../../types/staffNetwork';

export interface EngagementsTravelModalProps {
	isOpen: boolean;
	onClose: () => void;
	staffId: number | null;
	staffName: string;
	locationId: number | null;
}

const todayIso = (): string => {
	const now = new Date();
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
		now.getDate()
	).padStart(2, '0')}`;
};

const fmtTime = (iso: string): string =>
	new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const fmtDate = (iso: string | null): string => {
	if (!iso) return '';
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
};

const initialsOf = (name: string) =>
	name
		.split(' ')
		.filter(Boolean)
		.map(w => w[0])
		.join('')
		.slice(0, 2)
		.toUpperCase();

interface Gap {
	minutes: number;
	crossLocation: boolean;
	ok: boolean;
}

/** Gap between consecutive bookings; only a move between locations needs the travel time. */
const gapsOf = (entries: StaffDayPlanEntry[], travelMinutes: number): Gap[] => {
	const gaps: Gap[] = [];
	for (let i = 1; i < entries.length; i++) {
		const prev = entries[i - 1];
		const next = entries[i];
		const minutes = Math.round(
			(new Date(next.start).getTime() - new Date(prev.end).getTime()) / 60000
		);
		const crossLocation =
			prev.locationId != null && next.locationId != null && prev.locationId !== next.locationId;
		gaps.push({ minutes, crossLocation, ok: !crossLocation || minutes >= travelMinutes });
	}
	return gaps;
};

/**
 * Every salon a team member works at, and their bookings on one date across all of
 * them, so the 30-minute travel rule can be read off the day. Other salons' bookings
 * show the salon and the time only.
 */
const EngagementsTravelModal = ({
	isOpen,
	onClose,
	staffId,
	staffName,
	locationId,
}: EngagementsTravelModalProps) => {
	const [date, setDate] = useState(todayIso());
	const [data, setData] = useState<StaffEngagements | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!staffId || !locationId) return;
		setIsLoading(true);
		setError(null);
		try {
			setData(await staffNetworkApi.engagementsOf(staffId, locationId, date));
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load engagements');
		} finally {
			setIsLoading(false);
		}
	}, [staffId, locationId, date]);

	useEffect(() => {
		if (isOpen) {
			load();
		}
	}, [isOpen, load]);

	useEffect(() => {
		if (!isOpen) {
			setDate(todayIso());
			setData(null);
		}
	}, [isOpen]);

	const entries = useMemo(() => data?.dayPlan.entries ?? [], [data]);
	const travelMinutes = data?.dayPlan.travelMinutes ?? 30;
	const gaps = useMemo(() => gapsOf(entries, travelMinutes), [entries, travelMinutes]);
	const broken = gaps.filter(g => !g.ok).length;

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={`${staffName} — Engagements & Travel`} size="lg">
			{isLoading && !data ? (
				<div className="py-12 text-center">
					<Spinner size="lg" className="mx-auto text-purple-600" />
				</div>
			) : error ? (
				<p className="py-8 text-center text-sm text-red-500">{error}</p>
			) : data ? (
				<div className="space-y-6">
					{/* Works at */}
					<div>
						<p className="provider-form__label mb-2">Works at</p>
						<div className="space-y-2">
							{data.engagements.map(e => (
								<div
									key={e.staffId}
									className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
										e.mine
											? 'border-[#667eea] bg-indigo-50 dark:bg-indigo-900/20'
											: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
									}`}
								>
									<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
										{initialsOf(e.businessName)}
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2 flex-wrap">
											<span className="font-semibold text-gray-900 dark:text-gray-100">
												{e.businessName}
											</span>
											{e.mine && <Badge variant="primary">You</Badge>}
											{e.staffTypeName && (
												<Badge variant={e.staffType === 'ON_CALL' ? 'warning' : 'info'}>
													{e.staffTypeName}
												</Badge>
											)}
										</div>
										<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
											{e.locations
												.map(l =>
													[l.locationName, [l.city, l.state].filter(Boolean).join(', ')]
														.filter(Boolean)
														.join(' · ')
												)
												.join(' • ') || 'No locations'}
											{e.taskNames && e.taskNames.length > 0 && (
												<> • {e.taskNames.join(', ')}</>
											)}
											{e.hireDate && <> • since {fmtDate(e.hireDate)}</>}
										</div>
									</div>
									{e.mine && e.commissionRate != null && (
										<div className="text-sm font-bold text-indigo-700 dark:text-indigo-300 whitespace-nowrap">
											{e.commissionRate}%
										</div>
									)}
								</div>
							))}
						</div>
					</div>

					{/* Day plan */}
					<div>
						<div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
							<p className="provider-form__label mb-0">
								Day plan — {travelMinutes}-minute travel rule
							</p>
							<input
								type="date"
								className="provider-form__input !w-auto !py-1.5"
								value={date}
								onChange={e => setDate(e.target.value)}
							/>
						</div>

						{entries.length === 0 ? (
							<div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 px-4 py-3 text-sm">
								<div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-200">
									<CheckCircleIcon className="w-5 h-5" /> Nothing booked on {fmtDate(date)}
								</div>
								<p className="text-emerald-700 dark:text-emerald-300 mt-0.5">No travel to check.</p>
							</div>
						) : broken === 0 ? (
							<div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 px-4 py-3 text-sm mb-3">
								<div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-200">
									<CheckCircleIcon className="w-5 h-5" /> Travel gaps are fine
								</div>
								<p className="text-emerald-700 dark:text-emerald-300 mt-0.5">
									Every move between locations leaves at least {travelMinutes} minutes.
								</p>
							</div>
						) : (
							<div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm mb-3">
								<div className="flex items-center gap-2 font-semibold text-red-800 dark:text-red-200">
									<ExclamationTriangleIcon className="w-5 h-5" /> {broken} travel gap
									{broken === 1 ? '' : 's'} too short
								</div>
								<p className="text-red-700 dark:text-red-300 mt-0.5">
									Moving between locations needs at least {travelMinutes} minutes. Shift or
									reassign the flagged booking.
								</p>
							</div>
						)}

						{entries.length > 0 && (
							<div className="space-y-1">
								{entries.map((entry, i) => {
									const gap = i > 0 ? gaps[i - 1] : null;
									let gapText = '';
									let gapClass = 'text-gray-400';
									if (gap) {
										if (!gap.crossLocation) {
											gapText = `${gap.minutes} min gap • same location, no travel needed`;
										} else if (!gap.ok) {
											gapText = `${gap.minutes} min gap • needs ${travelMinutes} min to travel — short by ${
												travelMinutes - gap.minutes
											} min`;
											gapClass = 'text-red-600 dark:text-red-400 font-semibold';
										} else if (gap.minutes < travelMinutes + 15) {
											gapText = `${gap.minutes} min gap • just enough to travel`;
											gapClass = 'text-amber-600 dark:text-amber-400';
										} else {
											gapText = `${gap.minutes} min gap • travel between locations`;
										}
									}
									return (
										<div key={`${entry.start}-${i}`}>
											{gap && (
												<div className={`flex items-center gap-2 text-xs py-1 ${gapClass}`}>
													<span className="flex-1 border-t border-dashed border-gray-200 dark:border-gray-700" />
													<span>{gapText}</span>
													<span className="flex-1 border-t border-dashed border-gray-200 dark:border-gray-700" />
												</div>
											)}
											<div
												className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
													entry.mine
														? 'bg-indigo-50 dark:bg-indigo-900/20'
														: 'bg-gray-50 dark:bg-gray-800'
												}`}
											>
												<div className="text-xs font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap w-32">
													{fmtTime(entry.start)} – {fmtTime(entry.end)}
												</div>
												<div className="min-w-0 flex-1">
													<div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
														<MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
														<span className="truncate">
															{entry.mine
																? entry.locationName ?? entry.businessName
																: entry.businessName}
														</span>
														{entry.mine && <Badge variant="primary">You</Badge>}
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
														{entry.mine
															? [entry.serviceName, entry.customerName]
																	.filter(Boolean)
																	.join(' • ') || 'Booking'
															: 'Booked at another salon'}
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			) : null}
		</Modal>
	);
};

export default EngagementsTravelModal;
